import { useTheme } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { PatientTimelineEvent } from "@/pages/app/provider/patient/patient-list/patient-chart/chronological-records/use-chronological-record";
import { formatDateTime } from "@/utils/date-time-utility";
import LinkButton from "./LinkButton";

export interface ChronologyEventRowProps {
  event: PatientTimelineEvent;
  rowBg: string;
  actionLabel?: string;
  onAction?: (event: PatientTimelineEvent) => void;
}

// The backend sometimes emits titles with a trailing comma (e.g.
// "Visit Note Created,") — the row then appends ", {timestamp}" and renders a
// double comma. Strip any trailing comma/whitespace before appending.
const cleanTitle = (title?: string) => (title ?? "").replace(/[\s,]+$/, "");

// Descriptions occasionally interpolate a literal "null" for a missing value
// (e.g. "Chief Complaint: null"). Replace the stray token with a dash and trim
// trailing whitespace so the timeline never shows "null" to the user.
const cleanDescription = (description?: string) =>
  (description ?? "").replace(/:\s*null\b/gi, ": —").trim();

const ChronologyEventRow = ({
  event,
  rowBg,
  actionLabel,
  onAction,
}: ChronologyEventRowProps) => {
  const theme = useTheme();

  return (
    <Flex
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1.5,
        py: 0.5,
        backgroundColor: rowBg,
        borderLeft: `1px solid ${CHART_COLORS.border}`,
        borderRight: `1px solid ${CHART_COLORS.border}`,
        "&:hover": { backgroundColor: "#F2F6F8" },
      }}
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        sx={{
          flexShrink: 0,
          background: CHART_COLORS.surfaceAlt,
          borderRadius: "999px",
          width: 34,
          height: 34,
          fontSize: 16,
          color: theme.palette.primary.main,
        }}
      >
        {event.timelineEventTypeEmoji}
      </Flex>
      <Flex flexDirection="column" gap={0.5} flex={1} minWidth={0}>
        <Flex alignItems="center" gap={1}>
          <Text
            sx={{
              ...CHART_TYPOGRAPHY.body5,
              color: CHART_COLORS.textDark,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {cleanTitle(event.title)}
            {event.eventTimestamp &&
              `, ${formatDateTime(event.eventTimestamp)}`}
          </Text>
          {actionLabel && onAction && (
            <LinkButton onClick={() => onAction(event)}>
              {actionLabel}
            </LinkButton>
          )}
        </Flex>
        <Flex gap={1} alignItems="center">
          <Text
            sx={{
              ...CHART_TYPOGRAPHY.body5,
              color: CHART_COLORS.textLabel,
            }}
          >
            {event.eventTimestamp && formatDateTime(event.eventTimestamp)}
          </Text>
          <Text
            sx={{
              ...CHART_TYPOGRAPHY.body5,
              color: CHART_COLORS.textLabel,
            }}
          >
            |
          </Text>
          {cleanDescription(event.description) && (
            <Text
              sx={{
                ...CHART_TYPOGRAPHY.body5,
                color: CHART_COLORS.textLabel,
              }}
            >
              {cleanDescription(event.description)}
            </Text>
          )}
        </Flex>
      </Flex>
      {/* <ThreeDotMenu
        options={CHRONOLOGY_MENU_OPTIONS}
        iconButtonSx={{ alignSelf: "flex-start", p: 0.5 }}
      /> */}
    </Flex>
  );
};

export default ChronologyEventRow;
